import {
  OidcClient,
  UserManager,
  WebStorageStateStore,
  type User,
} from "oidc-client-ts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { readOidcConfig } from "./config";

export interface AuthUser {
  name: string;
}

export interface Auth {
  status: "authenticated" | "unauthenticated" | "loading";
  user: AuthUser | null;
  signIn: () => void;
  signOut: () => void;
  /** Current OIDC access token for API calls, or null when there is no session
      (or in the dev stub, where the local backend runs in dev-auth mode). */
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<Auth | null>(null);

/** Minimal slice of the OIDC `UserManager` this needs — keeps the helper testable. */
interface TokenSource {
  getUser: () => Promise<User | null>;
  signinSilent: () => Promise<User | null>;
}

type RenewHolder = { current: Promise<User | null> | null };

/** Return a valid access token, renewing on demand when the stored one is missing
    or expired. Renewal uses the refresh token (no iframe → survives Safari's
    third-party cookie blocking); concurrent callers share a single in-flight
    renew. Returns null only when there is genuinely no usable session — so a
    request never goes out tokenless just because a renewal was mid-flight (the
    cause of the intermittent 401s, OEM-285). */
export async function resolveAccessToken(
  mgr: TokenSource,
  holder: RenewHolder,
): Promise<string | null> {
  const u = await mgr.getUser();
  if (u && !u.expired) return u.access_token ?? null;
  if (!holder.current) {
    holder.current = mgr
      .signinSilent()
      .catch(() => null)
      .finally(() => {
        holder.current = null;
      });
  }
  const renewed = await holder.current;
  return renewed && !renewed.expired ? (renewed.access_token ?? null) : null;
}

/** Slice of `UserManager` the session wiring needs — kept structural so a fake
    can drive it in tests. The real `UserManager` satisfies it. */
export interface SessionManager {
  getUser: () => Promise<User | null>;
  signinSilent: () => Promise<User | null>;
  removeUser: () => Promise<void>;
  events: {
    addUserLoaded: (cb: (u: User) => void) => void;
    removeUserLoaded: (cb: (u: User) => void) => void;
    addUserUnloaded: (cb: () => void) => void;
    removeUserUnloaded: (cb: () => void) => void;
    addAccessTokenExpired: (cb: () => void) => void;
    removeAccessTokenExpired: (cb: () => void) => void;
    addSilentRenewError: (cb: (e: Error) => void) => void;
    removeSilentRenewError: (cb: (e: Error) => void) => void;
  };
}

/** Boot and keep an OIDC session alive across reloads (the store is persisted in
    localStorage). The rule that makes persistence safe: on ANY renewal failure,
    drop the stored session (`removeUser`) and fall to the portada — never leave a
    dead session in the store, or `automaticSilentRenew` retries it forever and
    hammers Keycloak's token endpoint (the Safari crash-loop that forced a revert
    of the first attempt, OEM-285).

    Calls `setUser(user | null)` as the session appears/disappears and
    `setLoading(false)` once the initial restore settles. Returns a cleanup fn. */
export function wireSession(
  mgr: SessionManager,
  setUser: (u: User | null) => void,
  setLoading: (loading: boolean) => void,
): () => void {
  let alive = true;
  const put = (u: User | null) => {
    if (alive) setUser(u);
  };
  // Wipe the persisted (now-unrenewable) session so a reload starts clean and no
  // renewal timer can loop on it, then show the portada.
  const clearSession = () => {
    void mgr.removeUser();
    put(null);
  };
  // Boot: the persisted store usually still holds a valid session after a reload
  // — hydrate from it instantly (no portada flash, no network round-trip). If it
  // is empty (fresh tab) or the token expired at rest, try one silent restore
  // and clear on failure so a dead session can't linger.
  void mgr
    .getUser()
    .then((u) => {
      if (u && !u.expired) {
        put(u);
        return;
      }
      return mgr
        .signinSilent()
        .then((r) => put(r))
        .catch(() => clearSession());
    })
    .catch(() => clearSession())
    .finally(() => {
      if (alive) setLoading(false);
    });

  const onLoaded = (u: User) => put(u);
  const onUnloaded = () => put(null);
  // Safety net for a live session: renew on expiry rather than drop the user
  // mid-use — but if the refresh is genuinely gone, clear the session instead of
  // retrying into a storm.
  const onExpired = () =>
    void mgr
      .signinSilent()
      .then((u) => put(u))
      .catch(() => clearSession());
  // `automaticSilentRenew` gave up: clear rather than let it keep retrying.
  const onSilentError = () => clearSession();
  mgr.events.addUserLoaded(onLoaded);
  mgr.events.addUserUnloaded(onUnloaded);
  mgr.events.addAccessTokenExpired(onExpired);
  mgr.events.addSilentRenewError(onSilentError);
  return () => {
    alive = false;
    mgr.events.removeUserLoaded(onLoaded);
    mgr.events.removeUserUnloaded(onUnloaded);
    mgr.events.removeAccessTokenExpired(onExpired);
    mgr.events.removeSilentRenewError(onSilentError);
  };
}

const nameOf = (u: User): string =>
  (u.profile.name as string | undefined) ??
  (u.profile.preferred_username as string | undefined) ??
  "Cuenta";

/** The short authorize URL after a Pushed Authorization Request: only `client_id`
    and the opaque `request_uri` handle travel in the browser — the rest of the
    request (redirect_uri, scope, state, PKCE challenge…) was pushed to Keycloak
    back-channel and is referenced by the handle. Pure, so it is unit-tested. */
export function buildParAuthorizeUrl(
  authorizationEndpoint: string,
  clientId: string,
  requestUri: string,
): string {
  const u = new URL(authorizationEndpoint);
  u.searchParams.set("client_id", clientId);
  u.searchParams.set("request_uri", requestUri);
  return u.toString();
}

/** Start sign-in through Pushed Authorization Requests (RFC 9126): build the
    normal signin request (which also stores the state + PKCE verifier where the
    callback will look for them), POST its parameters to Keycloak's PAR endpoint,
    and navigate to the short `client_id` + `request_uri` URL. Returns false — so
    the caller falls back to a plain redirect — when the server does not advertise
    PAR or the push is rejected; throws only on an unexpected fault (also caught by
    the caller). The full param set never appears in the address bar. */
async function signInWithPar(client: OidcClient, clientId: string): Promise<boolean> {
  const md = await client.metadataService.getMetadata();
  const parEndpoint = (md as Record<string, unknown>)[
    "pushed_authorization_request_endpoint"
  ] as string | undefined;
  const authEndpoint = md.authorization_endpoint;
  if (!parEndpoint || !authEndpoint) return false;
  // Creates AND persists the signin state (id, PKCE verifier, nonce) in the shared
  // localStorage store, so `signinRedirectCallback` completes the exchange exactly
  // as it does for a plain redirect. `req.url` carries the full param set.
  const req = await client.createSigninRequest({});
  const params = new URL(req.url).searchParams;
  const res = await fetch(parEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });
  if (!res.ok) return false;
  const { request_uri: requestUri } = (await res.json()) as { request_uri?: string };
  if (!requestUri) return false;
  window.location.assign(buildParAuthorizeUrl(authEndpoint, clientId, requestUri));
  return true;
}

/** Real OIDC (Authorization Code + PKCE) against Keycloak via a full-page
    redirect. The session is persisted in localStorage (see `wireSession` for the
    boot/renew rules that keep that safe); silent renew keeps the access token
    fresh.

    The authorization request is pushed (PAR, RFC 9126) so the browser URL carries
    only `client_id` + a short `request_uri` instead of the whole param set; a
    plain redirect (same outcome) is the fallback when PAR is unavailable.

    Redirect, not a popup, on purpose: Safari's WebKit crashes the opener's
    renderer when a cross-origin `window.open` popup is closed — merely opening the
    sign-in popup and dismissing it reloaded the whole app with "This web page was
    reloaded because a problem occurred". A redirect has no popup to close, no
    cross-window teardown and no opener `postMessage`, so it cannot hit that bug,
    and it is the same code path the app already recovers through after any
    reload. */
function RealAuthProvider({
  authority,
  clientId,
  children,
}: {
  authority: string;
  clientId: string;
  children: ReactNode;
}) {
  const mgrRef = useRef<UserManager | null>(null);
  const parClientRef = useRef<OidcClient | null>(null);
  if (!mgrRef.current) {
    const origin = window.location.origin;
    // One state store, shared by the UserManager and the PAR client below, so the
    // signin state the PAR path persists is the very store the callback reads.
    const stateStore = new WebStorageStateStore({ store: window.localStorage });
    const base = {
      authority,
      client_id: clientId,
      redirect_uri: `${origin}/acceso/callback`,
      response_type: "code",
      scope: "openid profile email",
      stateStore,
    };
    mgrRef.current = new UserManager({
      ...base,
      silent_redirect_uri: `${origin}/acceso/silent`,
      post_logout_redirect_uri: `${origin}/`,
      automaticSilentRenew: true,
      // Persist the session (user + refresh token) so a full page reload —
      // including Safari auto-reloading a crashed/idle tab — rehydrates it
      // instead of dropping to the portada. `wireSession` clears the store on
      // any renewal failure so a dead session can't drive a renew storm.
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
    // Bare OIDC client used only to build+push the authorization request (PAR).
    parClientRef.current = new OidcClient(base);
  }
  const mgr = mgrRef.current;
  const parClient = parClientRef.current!;
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => wireSession(mgr, setUser, setLoading), [mgr]);

  const signIn = useCallback(() => {
    // Full-page redirect to Keycloak (no popup → no Safari crash). Pushed via PAR
    // so the address bar shows only client_id + request_uri; a plain redirect is
    // the fallback (identical outcome) if PAR is unavailable or the push fails —
    // e.g. blocked by CORS. Either way the browser leaves this document and comes
    // back to `/acceso/callback`, which completes the exchange (see `RedirectCallback`).
    const plain = () => mgr.signinRedirect().catch(() => {});
    signInWithPar(parClient, clientId).then(
      (ok) => {
        if (!ok) plain();
      },
      () => plain(),
    );
  }, [mgr, parClient, clientId]);
  const signOut = useCallback(() => {
    // Real RP-initiated logout: end the Keycloak SSO session (end_session_endpoint)
    // and return to the portada. Only clearing the local user would leave the SSO
    // cookie alive, so the next "Entrar" would sign back in with no prompt.
    mgr.signoutRedirect().catch(() => {
      // Fallback if the redirect can't start: at least drop the local session.
      void mgr.removeUser();
      setUser(null);
    });
  }, [mgr]);
  const renewingRef = useRef<Promise<User | null> | null>(null);
  const getAccessToken = useCallback(
    () => resolveAccessToken(mgr, renewingRef),
    [mgr],
  );

  const value = useMemo<Auth>(
    () => ({
      // Authenticated as long as we hold a session (a `User`): a momentarily
      // expired access token is renewed transparently (on-demand in
      // `getAccessToken`, and by the expiry handlers above), so the shell must not
      // treat that transient window as a logout. We only fall to `unauthenticated`
      // when there is genuinely no user — never sign-in, an explicit sign-out, or a
      // failed renewal all clear it.
      status: loading ? "loading" : user ? "authenticated" : "unauthenticated",
      user: user ? { name: nameOf(user) } : null,
      signIn,
      signOut,
      getAccessToken,
    }),
    [user, loading, signIn, signOut, getAccessToken],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * DEV STUB — used only while the `orkestor-web` Keycloak client is not yet
 * provisioned (creating it needs sign-off; Keycloak is shared with Aqyra). Set
 * VITE_OIDC_AUTHORITY + VITE_OIDC_CLIENT_ID to switch to the real popup flow.
 */
function StubAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const signIn = useCallback(() => setUser({ name: "Agustín" }), []);
  const signOut = useCallback(() => setUser(null), []);
  // No real token in the stub: the local backend runs in dev-auth mode.
  const getAccessToken = useCallback(async () => null, []);
  const value = useMemo<Auth>(
    () => ({
      status: user ? "authenticated" : "unauthenticated",
      user,
      signIn,
      signOut,
      getAccessToken,
    }),
    [user, signIn, signOut, getAccessToken],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const cfg = readOidcConfig();
  return cfg ? (
    <RealAuthProvider authority={cfg.authority} clientId={cfg.clientId}>
      {children}
    </RealAuthProvider>
  ) : (
    <StubAuthProvider>{children}</StubAuthProvider>
  );
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
